import { defineStore } from 'pinia';
import { ref, computed, nextTick, watch } from 'vue';
import axios from '@/plugins/axios';
import router from '@/router';
import i18next from '@/config/i18n';
import { getDisplayedLang } from '@/utils/documentLang';
import { queryClient } from '@/plugins/vueQuery';
import { clearMediaImageCache } from '@/composables/useMediaImageCache';
import { buildLegacyMediaUrl } from '@/composables/useMediaUrl';
import { clearMediaVideoCache } from '@/composables/useMediaVideoCache';
import { useSnackbar } from '@/composables/useSnackbar';
import { useDataStore } from './data';
import { isExperimentProcessing } from '@/utils/experimentReadiness';
import { getStoredToken, setStoredToken, clearStoredToken } from '@/utils/authToken';
import { isPublicReportPath } from '@/utils/publicReportRoute';
import { tokenExpiresAt } from '@/utils/tokenExpiry';

// SYN-1694 — matriz de permissões: lista de áreas do staff, guardada como JSON.
// Qualquer valor inválido (ausente, corrompido, não-array) vira [] — sem área
// nenhuma a UI não mostra nada, que é o lado seguro.
const PERMISSIONS_STORAGE_KEY = 'user_permissions';

// SYN-1694 — áreas que têm superfície de ABA no painel admin. `impersonation`
// fica fora de propósito: ela libera o botão do olho dentro de telas que o
// staff alcança por outra área, não uma aba — staff só com ela não teria nada
// pra ver no painel (e tomaria um painel vazio + 403).
export const PANEL_AREAS = [
    'accounts',
    'subscriptions_billing',
    'waitlist',
    'logs_jobs',
    'legal_documents',
    'feedbacks',
    'dashboard',
];

// SYN-2757 — renova quando o token tem mais de 1h (faltam menos de 23h das 24h).
// Renew once the 24h token is over an hour old.
const RENEW_WHEN_REMAINING_MS = 23 * 60 * 60 * 1000;

function loadStoredPermissions() {
    try {
        const parsed = JSON.parse(localStorage.getItem(PERMISSIONS_STORAGE_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
}

// SYN-1865 — entitlements de features por plano. Mesmo desenho do
// `user_permissions`: chave própria, load defensivo, limpo no logout/setUser.
// A fonte é o `features[]` resolvido pelo backend no /subscriptions/my-usage —
// o SPA NUNCA re-deriva de `active_subscriptions[].plan`.
const FEATURES_STORAGE_KEY = 'user_features';

// SYN-1865 (review R1) — o valor guardado é `{ userId, features }`, não a lista
// crua: o login SSO seta o token direto e chama `fetchAndUpdateUserInfo()` sem
// passar por `setUser()`, então sem saber QUEM gravou as features elas
// sobreviveriam à troca de conta na mesma máquina. Com o dono junto dá pra
// distinguir F5 do mesmo usuário (mantém, sem piscar cadeado) de identidade
// nova (limpa, deny até o /my-usage responder).
function loadStoredFeatures() {
    try {
        const parsed = JSON.parse(localStorage.getItem(FEATURES_STORAGE_KEY));
        // Formato legado (lista crua, gravada antes do R1): adota o usuário
        // persistido como dono — é quem gravou — pra não piscar no deploy.
        if (Array.isArray(parsed)) return { userId: localStorage.getItem('user_id'), features: parsed };
        return {
            userId: parsed?.userId,
            features: Array.isArray(parsed?.features) ? parsed.features : [],
        };
    } catch (_) { return { userId: null, features: [] }; }
}

export const useUserStore = defineStore('user', () => {
    const token = ref(getStoredToken() || null);
    const clientId = ref(localStorage.getItem('client_id') || null);
    const userId = ref(localStorage.getItem('user_id') || null);
    const name = ref(localStorage.getItem('user_name') || '');
    const email = ref(localStorage.getItem('user_email') || '');
    const role = ref(localStorage.getItem('user_role') || 'user');
    // SYN-1694 — áreas de permissão do staff (vêm do /users/me). Persistidas
    // manualmente como o `role`: sem isso os menus piscam no F5, já que o
    // /users/me só responde depois do primeiro render.
    const permissions = ref(loadStoredPermissions());
    // SYN-1865 — features contratadas do cliente. Persistidas pelo mesmo motivo
    // das permissões: sem isso o cadeado pisca no F5 até o /my-usage responder.
    const _storedFeatures = loadStoredFeatures();
    const features = ref(_storedFeatures.features);
    // Dono das features persistidas (ver loadStoredFeatures). Só o
    // `persistFeatures` escreve — assim estado e storage nunca dessincronizam.
    let featuresOwnerId = _storedFeatures.userId;

    function persistFeatures() {
        featuresOwnerId = userId.value;
        localStorage.setItem(
            FEATURES_STORAGE_KEY,
            JSON.stringify({ userId: featuresOwnerId, features: features.value })
        );
    }

    // Identidade que responde o /users/me é a mesma que gravou as features?
    // Comparação por string: o /users/me devolve id numérico e o localStorage
    // guarda texto.
    function isFeaturesOwner(id) {
        return featuresOwnerId != null && String(featuresOwnerId) === String(id);
    }
    const authProvider = ref(localStorage.getItem('user_auth_provider') || 'local');
    const clientForceSso = ref(localStorage.getItem('client_force_sso') === 'true'); // Added
    const avatarUrl = ref(null);
    
    // v2.0 Subscription State (Aggregated)
    const aggregatedUsage = ref(null);
    const isLoadingUsage = ref(false);
    const usageError = ref(false);

    const termsAcceptanceRequired = ref(false);
    // Trilha legal do cliente: `terms_of_use` (Idril, padrão) ou `terms_of_use_legacy`
    // (Athena, contratos anteriores). Payload sem o campo = trilha atual.
    const DEFAULT_TERMS_DOC_TYPE = 'terms_of_use';
    const termsDocType = ref(DEFAULT_TERMS_DOC_TYPE);
    const latestTermsVersion = ref(null);
    const acceptedTermsVersion = ref(null);
    const showTermsUpdateNotice = ref(false);
    const newTermsVersionToAcknowledge = ref(null);
    const processingQueue = ref([]);
    const isLoadingQueue = ref(false);
    const activeUploads = ref(0);

    // --- Impersonation State ---
    const impersonatingUser = ref(null); // { id, name, email, client_id }
    const impersonationReturnRoute = ref(null); // Where to go back to

    function clearScopedCaches() {
        queryClient.clear();
        clearMediaImageCache();
        clearMediaVideoCache();
    }

    const isAuthenticated = computed(() => !!token.value);
    const isUploading = computed(() => activeUploads.value > 0);

    function beginUpload() { activeUploads.value++; }
    function endUpload() { activeUploads.value = Math.max(0, activeUploads.value - 1); }
    const isAdmin = computed(() => role.value === 'admin' || role.value === 'superadmin');
    const isSuperAdmin = computed(() => role.value === 'superadmin');
    // SYN-1694 — a defesa real é o 403 do backend; aqui a UI só acompanha.
    // `isAdmin`/`isSuperAdmin` NÃO mudam: staff não é nenhum dos dois (as abas
    // de organização do próprio tenant seguem invisíveis para ele).
    function hasArea(area) { return permissions.value.includes(area); }
    // SYN-1865 — gate de feature no SPA. Time interno (superadmin/staff) passa
    // sempre, inclusive impersonando: é a persona de suporte, mesmo bypass do
    // backend. Sem dado ainda (1º login) = deny — destrava quando o fetch
    // do /my-usage responde.
    function canUseFeature(feature) {
        return isSuperAdmin.value || role.value === 'staff' || features.value.includes(feature);
    }
    // Só entra no painel quem tem pelo menos UMA área com aba: `impersonation`
    // sozinha não abre o painel (ver PANEL_AREAS).
    const canAccessAdminPanel = computed(
        () => isSuperAdmin.value
            || (role.value === 'staff' && permissions.value.some(area => PANEL_AREAS.includes(area)))
    );
    const isImpersonating = computed(() => !!impersonatingUser.value);
    const isSSO = computed(() => authProvider.value !== 'local');
    
    const effectiveClientId = computed(() => {
        return isImpersonating.value ? impersonatingUser.value.client_id : clientId.value;
    });

    const userInitials = computed(() => {
        const nameParts = name.value?.split(' ') || [];
        if (nameParts.length === 1 && nameParts[0]) {
            return nameParts[0].substring(0, 2).toUpperCase();
        } else if (nameParts.length > 1) {
            return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
        }
        return '?';
    });
    const userAvatarSrc = computed(() => avatarUrl.value);

    // --- Mark-as-read persistence ----------------------------------------------
    // O backend (/notifications/mark-all-as-read) não está refletindo no `read`
    // retornado por /jobs/my-queue — F5 trazia tudo de volta como não lido.
    // Guardamos os IDs marcados-como-lidos no localStorage por usuário e
    // reaplicamos no fetch, então o estado sobrevive refresh sem depender do
    // backend. Limitamos a 500 IDs para não inchar o storage.
    const READ_JOBS_STORAGE_KEY = () => `synapsee:read-jobs:${userId.value || 'anon'}`;
    const MAX_READ_JOBS_TRACKED = 500;

    function loadReadJobIds() {
        try {
            const raw = localStorage.getItem(READ_JOBS_STORAGE_KEY());
            if (!raw) return new Set();
            const parsed = JSON.parse(raw);
            return new Set(Array.isArray(parsed) ? parsed : []);
        } catch (_) { return new Set(); }
    }

    function saveReadJobIds(set) {
        try {
            const arr = Array.from(set).slice(-MAX_READ_JOBS_TRACKED);
            localStorage.setItem(READ_JOBS_STORAGE_KEY(), JSON.stringify(arr));
        } catch (_) { /* localStorage indisponível: segue sem persistência */ }
    }

    function applyLocalReadState(jobs) {
        // processingQueue is consumed as a list everywhere (.some/.filter/.map);
        // coerce a malformed/non-array response to [] so a background watcher or
        // poll can't crash on it.
        if (!Array.isArray(jobs)) return [];
        if (jobs.length === 0) return jobs;
        const readIds = loadReadJobIds();
        if (readIds.size === 0) return jobs;
        return jobs.map(j => (j?.id && readIds.has(j.id)) ? { ...j, read: true } : j);
    }

    async function fetchProcessingQueue() {
        if (!isAuthenticated.value) return;
        // Multiple QueueStatus instances (header, dashboard, etc.) may fire this
        // simultaneously on mount. Skip if a fetch is already in flight.
        if (isLoadingQueue.value) return;
        isLoadingQueue.value = true;
        try {
            const response = await axios.get('/jobs/my-queue');
            // Reaplica o "lido" guardado localmente — backend ainda não persiste
            // (ver comentário no bloco de helpers acima).
            processingQueue.value = applyLocalReadState(response.data);
        } catch (error) {
            console.error("Error fetching processing queue:", error);
        } finally {
            isLoadingQueue.value = false;
        }
    }

    // Cancels a job stuck in awaiting_upload/invalid by experiment_id (the
     // primary key of the experiment, not the job_id). Backend route is
     // /experiments/{experiment_id}/cancel — it estornates the débito so
     // the user gets the credit back. Used by orphan recovery in the bell.
    async function cancelExperimentJob(experimentId) {
        if (!isAuthenticated.value) return;
        try {
            await axios.delete(`/experiments/${experimentId}/cancel`);
            processingQueue.value = processingQueue.value.filter(j =>
                j.experiment_id !== experimentId && j.id !== experimentId
            );
        } catch (error) {
            console.error(`Error cancelling experiment ${experimentId}:`, error);
            throw error;
        }
    }

    async function dismissJob(jobId) {
        if (!isAuthenticated.value) return;
        // DELETE /jobs/{id} accepts terminal statuses (completed, failed,
        // abandoned) per PR fix/dismiss-include-terminal-statuses on
        // synapsee_studio_backend. For active jobs (awaiting_upload,
        // queued, processing) callers must still use
        // DELETE /experiments/{job_id}/cancel — see useMediaUpload.js.
        // Abandoned jobs are dismissed automatically by FileBrowserDashboard
        // on fetch (the experiment has no next action and the credit is
        // already refunded).
        try {
            await axios.delete(`/jobs/${jobId}`);
            processingQueue.value = processingQueue.value.filter(j => j.id !== jobId);
        } catch (error) {
            console.error(`Error dismissing job ${jobId}:`, error);
            throw error;
        }
    }

    async function clearCompletedJobs() {
        if (!isAuthenticated.value) return;
        try {
            await axios.delete('/jobs/clear-completed');
            processingQueue.value = processingQueue.value.filter(
                j => j.status === 'queued' || j.status === 'processing'
            );
        } catch (error) {
            console.error("Error clearing completed jobs:", error);
            throw error;
        }
    }

    async function markAllNotificationsAsRead() {
        if (!isAuthenticated.value) return;
        // Persiste local — backend não está refletindo no /jobs/my-queue, F5
        // trazia tudo de volta como não lido (feedback 28/05).
        const readIds = loadReadJobIds();
        for (const j of processingQueue.value) {
            if (j?.id) readIds.add(j.id);
        }
        saveReadJobIds(readIds);
        // Atualização otimista: marca localmente antes da API
        processingQueue.value = processingQueue.value.map(j => ({
            ...j,
            read: true,
        }));
        try {
            // Mantemos o ping para o backend caso ele passe a persistir — o
            // localStorage cobre o gap enquanto isso.
            const response = await axios.patch('/notifications/mark-all-as-read');
            return response.data;
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    }

    // `remember` = "Manter conectado" (SYN-2738 U13): false keeps the token in
    // sessionStorage only.
    function setUser(userData, { remember = true } = {}) {
        token.value = userData.access_token;
        clientId.value = userData.client_id;
        userId.value = userData.user_id;
        name.value = userData.name;
        email.value = userData.email;
        role.value = userData.role || 'user';
        // SYN-1694 — simetria com o `role`: o payload de login/primeiro acesso
        // não traz `permissions`, então o campo é LIMPO aqui. Sem isso as áreas
        // do usuário anterior (ainda no localStorage) vazavam para o novo login
        // até o /users/me responder.
        permissions.value = Array.isArray(userData.permissions) ? userData.permissions : [];
        // SYN-1865 — o payload de login não traz `features`; limpar aqui evita
        // que as features do usuário anterior (ainda no localStorage) vazem
        // para o novo login até o /my-usage responder.
        features.value = [];

        termsAcceptanceRequired.value = userData.terms_acceptance_required || false;
        termsDocType.value = userData.terms_doc_type || DEFAULT_TERMS_DOC_TYPE;
        latestTermsVersion.value = userData.latest_terms_version || null;
        acceptedTermsVersion.value = termsAcceptanceRequired.value ? null : latestTermsVersion.value;

        setStoredToken(userData.access_token, remember);
        localStorage.setItem('client_id', userData.client_id);
        localStorage.setItem('user_id', userData.user_id);
        localStorage.setItem('user_name', userData.name);
        localStorage.setItem('user_email', userData.email);
        localStorage.setItem('user_role', role.value);
        localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions.value));
        persistFeatures();

        if (!termsAcceptanceRequired.value) {
            fetchAndUpdateUserInfo();
        }
    }

    function clearUser() {
        clearScopedCaches();
        token.value = null;
        clientId.value = null;
        userId.value = null;
        name.value = '';
        email.value = '';
        role.value = 'user';
        permissions.value = [];
        features.value = [];
        featuresOwnerId = null;
        authProvider.value = 'local';
        clientForceSso.value = false;
        avatarUrl.value = null;
        
        aggregatedUsage.value = null;
        isLoadingUsage.value = false;
        usageError.value = false;

        termsAcceptanceRequired.value = false;
        termsDocType.value = DEFAULT_TERMS_DOC_TYPE;
        latestTermsVersion.value = null;
        acceptedTermsVersion.value = null;
        showTermsUpdateNotice.value = false;
        newTermsVersionToAcknowledge.value = null;
        processingQueue.value = [];
        impersonatingUser.value = null;
        impersonationReturnRoute.value = null;

        clearStoredToken();
        localStorage.removeItem('client_id');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_name');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_role');
        localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
        localStorage.removeItem(FEATURES_STORAGE_KEY);
        localStorage.removeItem('user_auth_provider');
        localStorage.removeItem('client_force_sso');
    }

    async function fetchUserAvatar() {
        if (!userId.value || !clientId.value) {
            avatarUrl.value = null;
            return;
        }
        // SYN-1774: a rota estática agora exige JWT. O avatar não tem Media id
        // (arquivo user_{id}.jpg), então não passa pelo endpoint de signed URL
        // — vai sempre pela rota legada com ?token= (mesmo padrão do /ws).
        const url = buildLegacyMediaUrl(clientId.value, `user_${userId.value}.jpg`);
        try {
            const controller = new AbortController();
            const signal = controller.signal;
            const response = await fetch(url, { method: 'GET', signal, cache: 'no-store' });
            if (response.ok) {
                controller.abort();
                avatarUrl.value = `${url}${url.includes('?') ? '&' : '?'}t=${new Date().getTime()}`;
            } else {
                avatarUrl.value = null;
            }
        } catch (error) {
            // Silently fail for CORS/network errors - this is expected in development
            // The app will fallback to user initials
            avatarUrl.value = null;
        }
    }

    async function fetchSubscriptionUsage() {
        if (!isAuthenticated.value) return;
        isLoadingUsage.value = true;
        usageError.value = false;
        try {
            const response = await axios.get('/subscriptions/my-usage');
            aggregatedUsage.value = response.data.aggregated_usage;
            // SYN-1865 — fonte única das features (RF1). O campo mora DENTRO de
            // `aggregated_usage` (é do schema AggregatedUsageResponse), não na
            // raiz da resposta. Backend antigo sem o campo ⇒ [] (deny), nunca
            // 500 nem lista herdada.
            const resolvedFeatures = response.data.aggregated_usage?.features;
            features.value = Array.isArray(resolvedFeatures) ? resolvedFeatures : [];
            persistFeatures();
        } catch (error) {
            console.error("Error fetching subscription usage:", error);
            if (error.response?.status !== 401) {
                usageError.value = true;
            }
        } finally {
            isLoadingUsage.value = false;
        }
    }

    let _isFetchingUserInfo = false;

    async function fetchAndUpdateUserInfo() {
        if (_isFetchingUserInfo || !isAuthenticated.value) return;
        _isFetchingUserInfo = true;
        try {
            const response = await axios.get('/users/me', { _skipAuthRedirect: true });
                const userData = response.data;
                
                // SYN-1865 (review R1) — troca de identidade sem passar por
                // setUser() (login SSO/SAML: o Login.vue seta o token e cai
                // direto aqui). Se quem responde não é o dono das features
                // persistidas, limpa ANTES do fetchSubscriptionUsage: se o
                // /my-usage falhar, o usuário novo fica em deny em vez de
                // herdar as features do anterior. Mesmo usuário (F5) mantém —
                // é o anti-flicker do CA-F4.
                if (!isFeaturesOwner(userData.id)) features.value = [];

                userId.value = userData.id;
                clientId.value = userData.client_id;
                name.value = userData.name;
                email.value = userData.email;
                role.value = userData.role;
                // Payload legado (superadmin antes do SYN-1692) não traz o campo:
                // cai em [] e o gate de superadmin segue passando por cima.
                permissions.value = Array.isArray(userData.permissions) ? userData.permissions : [];
                authProvider.value = userData.auth_provider || 'local';
                clientForceSso.value = userData.client_force_sso || false; // Update force_sso
                acceptedTermsVersion.value = userData.accepted_terms_version;
                termsDocType.value = userData.terms_doc_type || DEFAULT_TERMS_DOC_TYPE;
                
                localStorage.setItem('user_id', userData.id);
                localStorage.setItem('client_id', userData.client_id);
                localStorage.setItem('user_name', name.value);
                localStorage.setItem('user_email', email.value);
                localStorage.setItem('user_role', role.value);
                localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions.value));
                // Reancora as features no usuário que acabou de se identificar
                // (limpas logo acima quando a identidade mudou).
                persistFeatures();
                localStorage.setItem('user_auth_provider', authProvider.value);
                localStorage.setItem('client_force_sso', clientForceSso.value);
                
                await fetchUserAvatar();
                await fetchSubscriptionUsage();
                await fetchProcessingQueue();
            } catch (error) {
                console.error("Error fetching user details:", error);
                if (error.response?.status === 401) {
                    // Stale token in localStorage on app boot: surface a
                    // snackbar and redirect to landing so the user isn't left
                    // on a half-broken protected page without context.
                    logout({ message: i18next.t('errors.unauthorized') });
                }
            } finally {
                _isFetchingUserInfo = false;
            }
    }

    // logout({ message }): when called from forced paths (401 interceptor, WS
    // auth fail, critical 403), pass a `message` to surface a snackbar so the
    // user knows why they were kicked out. Voluntary callers (logout button,
    // terms-decline) pass nothing and the redirect happens silently.
    function logout(opts = {}) {
        const { message } = opts;
        const routerInstance = router;
        clearUser();
        if (message) {
            const { showSnackbar } = useSnackbar();
            showSnackbar(message, 'warning', 5000);
        }
        nextTick(() => {
             routerInstance.replace('/').catch(err => {});
        });
    }

    // SYN-2755 — mantém as abas em sincronia pelo localStorage (o evento
    // `storage` só dispara nas OUTRAS abas). Outra aba deslogou → desloga aqui;
    // outra aba logou de novo → adota o token novo, ou recarrega se o usuário
    // mudou (o estado em memória desta aba ainda é do usuário anterior).
    // Keeps tabs in sync: follow a logout, adopt a newer token, reload on user switch.
    function syncFromStorage(event) {
        if (event.key !== 'token' && event.key !== 'user_id') return;
        const storedToken = getStoredToken();
        if (!storedToken) {
            if (token.value) logout();
            return;
        }
        const storedUserId = localStorage.getItem('user_id');
        if (storedUserId && storedUserId !== userId.value) {
            window.location.reload();
            return;
        }
        token.value = storedToken;
    }

    // SYN-2757 — renovação deslizante. Só o uso real chama isto (clique/tecla, no
    // App.vue); requests automáticas (WS, polling) não. Assim o token vence 24h
    // depois do último uso, e o backend corta 30 dias após o login. Se a troca
    // falhar, não tenta de novo com o mesmo token: ele segue até vencer.
    // Sliding renewal driven by user activity only; never retries a refused token.
    let isRenewing = false;
    let refusedToken = null;
    async function renewTokenIfStale() {
        const current = getStoredToken();
        if (!current || isRenewing || current === refusedToken) return;
        const expiresAt = tokenExpiresAt(current);
        if (expiresAt === null || expiresAt - Date.now() > RENEW_WHEN_REMAINING_MS) return;
        isRenewing = true;
        try {
            const response = await axios.post('/token/refresh', null, { skipImpersonation: true, _skipAuthRedirect: true });
            // Outra aba pode ter deslogado/logado durante a troca: só grava sobre o
            // mesmo token, e no mesmo storage ("Manter conectado", SYN-2738).
            if (getStoredToken() === current) {
                setStoredToken(response.data.access_token, localStorage.getItem('token') === current);
                token.value = response.data.access_token;
            }
        } catch (error) {
            console.warn('Token renewal refused:', error);
            refusedToken = current;
        } finally {
            isRenewing = false;
        }
    }

    function markTermsAsAccepted() {
        termsAcceptanceRequired.value = false;
        acceptedTermsVersion.value = latestTermsVersion.value;
        fetchAndUpdateUserInfo();
    }

    function setTermsUpdateRequired(newVersion) {
        if (acceptedTermsVersion.value !== newVersion) {
            showTermsUpdateNotice.value = true;
            newTermsVersionToAcknowledge.value = newVersion;
        }
    }

    async function acknowledgeTermsUpdate() {
        if (!newTermsVersionToAcknowledge.value) return;
        try {
            // language = the language actually shown to the user. The terms
            // update can be acknowledged from the snackbar "OK" (no modal), so
            // we resolve it from published_langs + current locale, falling back
            // to PT when the active locale has no published translation.
            const language = await resolveDisplayedTermsLang();
            await axios.post('/users/me/accept-terms', {
                version: newTermsVersionToAcknowledge.value,
                language,
            });
            acceptedTermsVersion.value = newTermsVersionToAcknowledge.value;
            latestTermsVersion.value = newTermsVersionToAcknowledge.value;
            showTermsUpdateNotice.value = false;
            newTermsVersionToAcknowledge.value = null;
        } catch (error) {
            console.error("Error acknowledging terms update:", error);
        }
    }

    async function resolveDisplayedTermsLang() {
        try {
            const meta = await axios.get(`/documents/${termsDocType.value}/active-meta`);
            return getDisplayedLang(meta.data?.published_langs || ['pt']);
        } catch (_) {
            return getDisplayedLang(['pt']);
        }
    }

    function startImpersonation(targetUser, redirectLocation = '/main', returnLocation = null) {
        // SYN-1694 — staff com a área `impersonation` também impersona; os
        // gatilhos indiretos (LogViewer, timeline, ações de criativo) chamam
        // esta função sem checar papel, então o gate mora aqui.
        if (!isSuperAdmin.value && !hasArea('impersonation')) return;
        clearScopedCaches();
        impersonatingUser.value = {
            id: targetUser.id,
            name: targetUser.name,
            email: targetUser.email,
            client_id: targetUser.client_id
        };
        impersonationReturnRoute.value = returnLocation;
        // SYN-1865 v1.1 (RF13.2): até o /my-usage do impersonado responder, as
        // features eram as do superadmin — um round-trip com a UI destravada no
        // tenant errado (e destravada PARA SEMPRE se o fetch falhar). Janela sem
        // dado = deny, o mesmo desenho do RF2.
        features.value = [];
        fetchSubscriptionUsage();
        router.push(redirectLocation);
    }

    function stopImpersonation() {
        clearScopedCaches();
        const lastClientId = impersonatingUser.value?.client_id;
        impersonatingUser.value = null;
        features.value = []; // idem: as do cliente impersonado não são as do usuário real
        fetchSubscriptionUsage();
        
        // Determine return route
        let nextRoute;
        if (impersonationReturnRoute.value) {
            nextRoute = impersonationReturnRoute.value;
            impersonationReturnRoute.value = null;
        } else if (lastClientId) {
            nextRoute = {
                name: 'CustomerDetails',
                params: { clientId: lastClientId },
                query: { tab: 'users' }
            };
        } else {
            nextRoute = { name: 'SuperAdminPanel', query: { tab: 'clients' } };
        }

        router.push(nextRoute);
    }

    // SYN-2721: na página pública do relatório (`/r/:token`) o visitante não tem
    // sessão a validar — e um token velho daria 401 e logout por cima do relatório.
    if (isAuthenticated.value && !isPublicReportPath()) {
        fetchAndUpdateUserInfo();
    }

    // --- Queue polling fallback (SYN-435) ----------------------------------------
    // O WS pode falhar no fim do pipeline — conexão dropada, evento não emitido,
    // race no broadcast horizontal, worker derrubado — e o cliente fica sem
    // feedback ("precisa F5"). A reconciliação por processingQueue (Main.vue) já
    // cura quando algum trigger refaz o fetch; este polling é a rede de segurança
    // pro caso de NENHUM trigger disparar (WS connected mas backend silencioso).
    // Roda 30s, só quando há job ativo, dorme com tab hidden, retoma no visible.
    const QUEUE_POLL_INTERVAL_MS = 30000;
    const QUEUE_ACTIVE_STATUSES = ['queued', 'pending', 'validating', 'processing'];
    let _queuePollHandle = null;

    const _hasActiveJobs = () => {
        // `j?.status` espelha o null-guard do applyLocalReadState — entrada null na
        // fila não pode derrubar o watcher de polling.
        // Jobs cuja ANÁLISE ainda roda (estado clássico da fila).
        if (processingQueue.value.some(j => QUEUE_ACTIVE_STATUSES.includes(j?.status))) return true;
        // SYN-644 (re-fix): o pipeline downstream (optimizer/insight/attention_map —
        // os stages "neurais") roda DEPOIS que o job de análise vira 'completed' na
        // fila e é INVISÍVEL pro job.status. Antes o polling parava assim que a
        // análise terminava; se um WS terminal de um stage downstream era dropado
        // (comum no fluxo de "vários arquivos seguidos", muito tráfego WS) nada mais
        // refazia o fetch e o card ficava preso em "Processando" pra sempre. Mantém
        // a rede de segurança viva enquanto algum criativo da fila ainda não está
        // 100% pronto. useDataStore() é chamado lazy (runtime) pra evitar ciclo de init.
        const dataStore = useDataStore();
        return processingQueue.value.some(j =>
            isExperimentProcessing(dataStore.findItemById(j?.experiment_id))
        );
    };

    function _stopQueuePoll() {
        if (_queuePollHandle) {
            clearInterval(_queuePollHandle);
            _queuePollHandle = null;
        }
    }

    function _startQueuePollIfNeeded() {
        if (_queuePollHandle) return;
        _queuePollHandle = setInterval(() => {
            if (typeof document !== 'undefined' && document.hidden) return;
            if (!isAuthenticated.value) { _stopQueuePoll(); return; }
            if (!_hasActiveJobs()) { _stopQueuePoll(); return; }
            fetchProcessingQueue();
        }, QUEUE_POLL_INTERVAL_MS);
    }

    // Liga/desliga conforme entram/saem jobs ativos. `immediate` cobre o caso de
    // já existir job ativo na primeira hydratação da fila.
    watch(processingQueue, () => {
        if (!isAuthenticated.value) { _stopQueuePoll(); return; }
        if (_hasActiveJobs()) _startQueuePollIfNeeded();
        else _stopQueuePoll();
    }, { deep: true, immediate: true });

    // Logout para o polling.
    watch(isAuthenticated, (auth) => { if (!auth) _stopQueuePoll(); });

    // Quando a tab volta a ficar visível, não esperamos o próximo tick de 30s —
    // refazemos o fetch na hora se ainda houver job ativo. Single-shot listener,
    // store é singleton.
    /* istanbul ignore else -- document is always defined in jsdom/browser; else arm only reachable in SSR/workers */
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            if (!isAuthenticated.value) return;
            if (_hasActiveJobs()) fetchProcessingQueue();
        });
    }

    return {
        token, clientId, userId, name, email, role, permissions, features, authProvider, clientForceSso,
        avatarUrl, 
        aggregatedUsage, isLoadingUsage, usageError,
        termsAcceptanceRequired, termsDocType, latestTermsVersion, acceptedTermsVersion,
        showTermsUpdateNotice, newTermsVersionToAcknowledge,
        processingQueue, isLoadingQueue, activeUploads, isUploading,
        beginUpload, endUpload,
        isAuthenticated, isAdmin, isSuperAdmin, isSSO,
        hasArea, canAccessAdminPanel, canUseFeature,
        userInitials, userAvatarSrc,
        impersonatingUser, isImpersonating, effectiveClientId,
        setUser, clearUser, fetchUserAvatar, syncFromStorage, renewTokenIfStale,
        fetchSubscriptionUsage, fetchAndUpdateUserInfo,
        logout, markTermsAsAccepted,
        setTermsUpdateRequired, acknowledgeTermsUpdate,
        fetchProcessingQueue,
        cancelExperimentJob,
        dismissJob, clearCompletedJobs, markAllNotificationsAsRead,
        startImpersonation, stopImpersonation
    };
});
